import { useCallback, useEffect, useRef, useState } from "react"

import { cleanCanvas } from "../utils/notebookCanvas"
import {
  getNotebook,
  NotebookConflictError,
  updateNotebookDocument,
} from "../utils/notebooksApi"
import type { Notebook, NotebookDocument } from "../utils/notebookSnapshot"

const initial = (notebook: Notebook): NotebookDocument =>
  notebook.document ?? {
    version: 1,
    title: notebook.title,
    summary: "",
    sections: [],
    caveats: [],
  }
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

/** One writer at a time. A failed save retains the draft and pauses autosave. */
export function useNotebookDocument({
  notebook,
  organizationSlug,
  workspaceId,
  onSaved,
  saveDocument = updateNotebookDocument,
  loadNotebook = getNotebook,
}: {
  notebook: Notebook
  organizationSlug: string
  workspaceId: string
  onSaved: (notebook: Notebook) => void
  saveDocument?: typeof updateNotebookDocument
  loadNotebook?: typeof getNotebook
}) {
  const [document, setDocument] = useState(() => initial(notebook))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [conflictDetected, setConflictDetected] = useState(false)
  const [conflict, setConflict] = useState<Notebook | null>(null)
  const [loadingLatest, setLoadingLatest] = useState(false)
  const [history, setHistory] = useState<{
    past: NotebookDocument[]
    future: NotebookDocument[]
  }>({ past: [], future: [] })
  const current = useRef(notebook)
  const draft = useRef(document)
  const flight = useRef(false)
  const mounted = useRef(true)
  const lastEdit = useRef(0)
  const loading = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  useEffect(() => {
    if (notebook.updated_at === current.current.updated_at) {
      return
    }
    const clean = same(draft.current, initial(current.current))
    if (clean) {
      current.current = notebook
      const next = initial(notebook)
      if (!same(next, draft.current)) {
        const previous = draft.current
        setHistory((value) => ({
          past: [...value.past, previous].slice(-100),
          future: [],
        }))
        draft.current = next
        setDocument(next)
        lastEdit.current = 0
      }
    } else {
      setConflictDetected(true)
      setConflict(notebook)
      setError(
        "This notebook changed while you were editing. Review the saved version before choosing which changes to keep.",
      )
    }
  }, [notebook])
  const change = useCallback((next: NotebookDocument) => {
    if (same(next, draft.current)) {
      return
    }
    const previous = draft.current
    const grouped = Date.now() - lastEdit.current < 800
    setHistory((value) => ({
      past: grouped ? value.past : [...value.past, previous].slice(-100),
      future: [],
    }))
    lastEdit.current = Date.now()
    draft.current = next
    setDocument(next)
  }, [])
  const travel = (direction: "past" | "future") => {
    const stack = history[direction]
    if (!stack.length || saving) {
      return
    }
    const next = stack[stack.length - 1]
    setHistory(
      direction === "past"
        ? {
            past: stack.slice(0, -1),
            future: [...history.future, draft.current],
          }
        : {
            past: [...history.past, draft.current],
            future: stack.slice(0, -1),
          },
    )
    draft.current = next
    setDocument(next)
    lastEdit.current = 0
  }
  const loadLatest = useCallback(async () => {
    if (loading.current) {
      return
    }
    loading.current = true
    setLoadingLatest(true)
    try {
      const latest = await loadNotebook(
        organizationSlug,
        workspaceId,
        current.current.id,
      )
      if (mounted.current) {
        setConflict(latest)
      }
    } catch {
      if (mounted.current) {
        setError(
          "Could not load the saved version. Your draft is intact. Try again.",
        )
      }
    } finally {
      loading.current = false
      if (mounted.current) {
        setLoadingLatest(false)
      }
    }
  }, [loadNotebook, organizationSlug, workspaceId])
  const save = useCallback(
    async (locked?: boolean, reviewed?: Notebook) => {
      if (
        flight.current ||
        (conflictDetected && !reviewed) ||
        reviewed?.locked
      ) {
        return false
      }
      flight.current = true
      setSaving(true)
      setError("")
      const sent = draft.current
      try {
        const saved = await saveDocument(
          organizationSlug,
          workspaceId,
          reviewed ?? current.current,
          sent.content ? { ...sent, content: cleanCanvas(sent.content) } : sent,
          locked,
        )
        if (!mounted.current) {
          return false
        }
        current.current = saved
        setConflictDetected(false)
        setConflict(null)
        if (same(draft.current, sent)) {
          draft.current = initial(saved)
          setDocument(draft.current)
        }
        onSaved(saved)
        return true
      } catch (failure) {
        if (mounted.current) {
          if (failure instanceof NotebookConflictError) {
            setConflictDetected(true)
            setConflict(null)
            setError(
              "This notebook changed while you were editing. Review the saved version before choosing which changes to keep.",
            )
            await loadLatest()
            return false
          }
          setError(
            failure instanceof Error
              ? failure.message
              : "Could not save. Your draft is intact.",
          )
        }
        return false
      } finally {
        flight.current = false
        if (mounted.current) {
          setSaving(false)
        }
      }
    },
    [
      conflictDetected,
      loadLatest,
      onSaved,
      organizationSlug,
      saveDocument,
      workspaceId,
    ],
  )
  const dirty = !same(document, initial(current.current))
  useEffect(() => {
    if (!dirty || saving || error || notebook.locked) {
      return
    }
    const timer = setTimeout(() => void save(), 650)
    return () => clearTimeout(timer)
  }, [dirty, document, saving, error, notebook.locked, save])
  return {
    document,
    change,
    dirty,
    saving,
    error,
    save,
    conflict,
    conflictDetected,
    loadLatest,
    loadingLatest,
    saveDraftInstead: () =>
      conflict ? save(undefined, conflict) : Promise.resolve(false),
    useSavedVersion: () => {
      if (!conflict || flight.current) {
        return
      }
      current.current = conflict
      draft.current = initial(conflict)
      setDocument(draft.current)
      setHistory({ past: [], future: [] })
      setConflictDetected(false)
      setConflict(null)
      setError("")
      lastEdit.current = 0
      onSaved(conflict)
    },
    undo: () => travel("past"),
    redo: () => travel("future"),
    canUndo: !!history.past.length,
    canRedo: !!history.future.length,
  }
}
